import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["sx", "color", "colorsUnchecked", "disabled", "svgContentUnchecked", "svgContentChecked"];
import PropTypes from 'prop-types';
import * as React from 'react';
import { alpha } from '@mui/material';
import MuiSwitch from '@mui/material/Switch';
import { extendSX } from '../helpers';
import { jsx as _jsx } from "react/jsx-runtime";
function getIconColor(theme, {
  color,
  disabled
}) {
  const colorKey = !color || color === 'default' ? 'primary' : color;
  // @ts-ignore
  let result = theme.palette[colorKey].main;
  if (disabled) {
    result = alpha(result, 0.5);
  }
  return result;
}
export const SwitchSVGContentDisabled = 'M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z';
export const SwitchSVGContentCheck = 'M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z';
export const SwitchSVGContentNotification = 'M20 18.69 7.84 6.14 5.27 3.49 4 4.76l2.8 2.8v.01c-.52.99-.8 2.16-.8 3.42v5l-2 2v1h13.73l2 2L21 19.72l-1-1.03zM12 22c1.11 0 2-.89 2-2h-4c0 1.11.89 2 2 2zm6-7.32V11c0-3.08-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68c-.15.03-.29.08-.42.12-.1.03-.2.07-.3.11h-.01c-.01 0-.01 0-.02.01-.23.09-.46.2-.68.31 0 0-.01 0-.01.01L18 14.68z';
export const SwitchSVGContentLock = 'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z';

/**
 *
 * Demos:
 *
 * - [Switch](https://ui-packages.isbuildingluma.com/ui-core/react-switch/)
 *
 * API:
 *
 * - [Switch API](https://ui-packages.isbuildingluma.com/ui-core/api/switch/)
 * - inherits [MuiSwitch API](https://mui.com/material-ui/api/switch/)
 */
const Switch = /*#__PURE__*/React.forwardRef(function Switch(_ref, ref) {
  let {
      sx,
      color,
      colorsUnchecked = 'default',
      disabled,
      svgContentUnchecked,
      svgContentChecked
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(MuiSwitch, _extends({
    ref: ref
    // Casting because we want to avoid using `SwitchPropsColorOverrides`
    // https://mui.com/material-ui/customization/theme-components/#creating-new-component-variants
    ,
    color: color,
    disabled: disabled,
    sx: extendSX(sx, theme => _extends({}, svgContentChecked && {
      '& .Mui-checked .MuiSwitch-thumb:before': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20px" width="18px" viewBox="0 0 30 30"><path fill="${encodeURIComponent(`${getIconColor(theme, {
          color,
          disabled
        })}`)}" d="${svgContentChecked}"/></svg>')`
      }
    }, svgContentUnchecked && {
      '& .MuiSwitch-thumb:before': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="10" width="10" viewBox="0 0 20 20"><path fill="${encodeURIComponent(colorsUnchecked === 'opposited' ? theme.palette.grey[600] : theme.palette.common.white)}" d="${svgContentUnchecked}"/></svg>')`
      }
    }, colorsUnchecked === 'opposited' && {
      '& .MuiSwitch-switchBase:not(.Mui-checked)+.MuiSwitch-track': {
        backgroundColor: disabled ? `${alpha(theme.palette.grey[600], 0.5)}!important` : `${theme.palette.grey[600]}!important`
      },
      '& .MuiSwitch-thumb': {
        backgroundColor: theme.palette.common.white
      }
    }))
  }, rest));
});
process.env.NODE_ENV !== "production" ? Switch.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit "SwitchProps.ts" and run "yarn proptypes"
  // ----------------------------------------------------------------------
  /**
   * If `true`, the component is checked.
   */
  checked: PropTypes.bool,
  /**
   * @ignore
   */
  children: PropTypes.node,
  /**
   * The color of the component.
   */
  color: PropTypes.oneOf(['default', 'error', 'info', 'primary', 'quaternary', 'secondary', 'success', 'tertiary', 'warning']),
  /**
   * The colors of the component when unchecked
   * @default 'default'
   */
  colorsUnchecked: PropTypes.oneOf(['default', 'opposited']),
  /**
   * The default checked state. Use when the component is not controlled.
   */
  defaultChecked: PropTypes.bool,
  /**
   * If `true`, the component is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the ripple effect is disabled.
   */
  disableRipple: PropTypes.bool,
  /**
   * If given, uses a negative margin to counteract the padding on one
   * side (this is often helpful for aligning the left or right
   * side of the icon with content above or below, without ruining the border
   * size and shape).
   * @default false
   */
  edge: PropTypes.oneOf(['end', 'start', false]),
  /**
   * The id of the `input` element.
   */
  id: PropTypes.string,
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element.
   */
  inputProps: PropTypes.object,
  /**
   * Pass a ref to the `input` element.
   */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any.isRequired
  })]),
  /**
   * Callback fired when the state is changed.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   * You can pull out the new checked state by accessing `event.target.checked` (boolean).
   */
  onChange: PropTypes.func,
  /**
   * If `true`, the `input` element is required.
   */
  required: PropTypes.bool,
  /**
   * The SVG content we want to show in the thumb when it's checked
   */
  svgContentChecked: PropTypes.string,
  /**
   * The SVG content we want to show in the thumb when it's unchecked
   */
  svgContentUnchecked: PropTypes.string,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * The value of the component. The DOM API casts this to a string.
   * The browser uses "on" as the default value.
   */
  value: PropTypes.any
} : void 0;
export default Switch;